import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { GetUserDetailsResponse, UserDetails } from "@ServerTypes/accounts/accounts.schema";

const initialState: UserDetails = {};

/**
 * One `/accounts/getUserDetails` row as this slice stores it, for web's action and mobile's two
 * dispatch sites alike. The route passes stored nulls through, while `userDetailsSchema` declares
 * those members optional only, so a null becomes an absent value — what readers already get for an
 * account that lacks the key. `isRegistered` is 0/1 on most accounts that carry it, and is read
 * nowhere in web or mobile. Every other member is passed through unchanged.
 */
export const toUserDetails = ({
  organisation,
  ...row
}: NonNullable<GetUserDetailsResponse["result"][number]>): UserDetails => ({
  ...row,
  firstName: row.firstName ?? undefined,
  lastName: row.lastName ?? undefined,
  email: row.email ?? undefined,
  contactNo: row.contactNo ?? undefined,
  designation: row.designation ?? undefined,
  location: row.location ?? undefined,
  profilePic: row.profilePic ?? undefined,
  signature: row.signature ?? undefined,
  messageFooter: row.messageFooter ?? undefined,
  isRegistered: row.isRegistered == null ? undefined : Boolean(row.isRegistered),
  ...(organisation && {
    organisation: { ...organisation, id: organisation.id ?? undefined, name: organisation.name ?? undefined },
  }),
});

const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    setUserDetails: (_state: UserDetails, action: PayloadAction<UserDetails>) => ({ ...action.payload }),
    updateUserDetails: (state: UserDetails, action: PayloadAction<Partial<UserDetails>>) => ({
      ...state,
      ...action.payload,
    }),
    resetUserDetails: () => initialState,
  },
});

const { setUserDetails: _setUserDetailsRaw, updateUserDetails: _updateUserDetailsRaw } = userDetailsSlice.actions;
export const { resetUserDetails } = userDetailsSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setUserDetails = <T extends Exact<UserDetails, T>>(payload: T) => _setUserDetailsRaw(payload);
export const updateUserDetails = <T extends Exact<Partial<UserDetails>, T>>(payload: T) =>
  _updateUserDetailsRaw(payload);

export default userDetailsSlice.reducer;
